import environment from '../../environments';

// NOTE: for migration from window.gapi to window.google,
// see https://developers.google.com/identity/gsi/web/guides/migration
export async function getGoogleAuth() {
  const instance = new GoogleAuth({
    clientId: environment.googleAuthConfig?.clientId
  });
  await instance.ensureLoadedScript();
  return instance;
}

export interface GoogleAuthOptions {
  clientId?: string;
}

export interface GoogleAuthRenderButtonOptions {
  parent: HTMLElement;
  clientId?: string;
  onClick?: () => void;
  onResponse?: (response: google.accounts.id.CredentialResponse) => void;
}

export class GoogleAuth {
  protected readonly gsiSrc = 'https://accounts.google.com/gsi/client';

  constructor(protected readonly options: GoogleAuthOptions = {}) {}

  // check if window.google is set or check for existing script
  isLoaded() {
    return !!(
      window.google || document.querySelector(`script[src="${this.gsiSrc}"]`)
    );
  }

  async ensureLoadedScript() {
    try {
      if (await this.loadScript()) {
        console.debug(
          '[google] Script loaded. window.google:',
          !!window.google
        );
      }
    } catch (error) {
      console.error('[google] Failed to load script.', error);
    }
  }

  protected loadScript() {
    // resolve to false if already loaded
    return new Promise<boolean>((resolve, reject) => {
      // skip if script already exists
      if (this.isLoaded()) {
        resolve(false);
        return;
      }
      const script = document.createElement('script');
      script.async = true;
      script.src = this.gsiSrc;
      script.onload = () => resolve(true);
      script.onerror = error => reject(error);
      document.head.appendChild(script);
    });
  }

  /**
   * Render the Google login button instead of using a custom button
   * since there is currently no direct way to trigger opening a separate
   * popup window for the Google login except for the Google login button.
   * @returns The render button options.
   * @returns The result if there were any errors.
   */
  renderButton(options: GoogleAuthRenderButtonOptions): {
    ok: boolean;
    error?: string;
  } {
    const clientId = options.clientId || this.options.clientId;
    const id = window.google?.accounts?.id;
    if (!clientId || !id) {
      const error = !clientId
        ? 'Missing Client ID configuration.'
        : !id
        ? 'Google login method not initialized.'
        : undefined;
      console.error('[google]', error);
      return { ok: false, error };
    }

    id.initialize({
      client_id: clientId,
      context: 'signin',
      native_callback(response) {
        console.debug('[google] Sign In native response:', response);
        options.onResponse?.(response);
      },
      callback(response) {
        console.debug('[google] Sign In response:', response);
        options.onResponse?.(response);
      }
    });
    id.renderButton(options.parent, {
      type: 'icon',
      size: 'large',
      theme: 'outline',
      shape: 'circle',
      click_listener: () => options.onClick?.()
    });
    return { ok: true };
  }
}
